// centralized api utility with auth handling
// handles 401 responses and token refresh

export class SessionExpiredError extends Error {
    constructor(message = 'Session expired') {
        super(message);
        this.name = 'SessionExpiredError';
    }
}

// get api url based on environment
// REACT_APP_DEPLOY_ENV is set at build time by deploy:qa / deploy:prod scripts.
// NODE_ENV only controls build optimization (always 'production' for any build).
export const getApiUrl = () => {
    if (process.env.REACT_APP_DEPLOY_ENV === 'production') {
        return process.env.REACT_APP_API_URL_PROD_ADVISOR;
    }
    if (process.env.REACT_APP_DEPLOY_ENV === 'qa') {
        return process.env.REACT_APP_API_URL_QA;
    }
    if (process.env.NODE_ENV === 'production' && !process.env.REACT_APP_DEPLOY_ENV) {
        console.error(
            'WARNING: Production build without REACT_APP_DEPLOY_ENV. ' +
            'API will fall back to localhost. Use npm run deploy:qa or deploy:prod instead.'
        );
    }
    return process.env.REACT_APP_API_URL_LOCAL;
};

export const optionalAuthHeader = (currentUser) =>
    currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {};

export const optionalAuthFetch = (url, options, authParams) =>
    authParams?.currentUser?.token ? authFetch(url, options, authParams) : fetch(url, options);

/**
 * Authenticated fetch wrapper
 * @param {string} url - API endpoint URL
 * @param {object} options - fetch options (method, body, etc.)
 * @param {object} params - auth params
 * @param {object} params.currentUser - current user from Redux (must have token)
 * @param {function} params.onSessionExpired - callback when 401 is received
 * @param {function} params.onTokenRefresh - callback when new token is received (optional)
 * @returns {Promise<Response>} fetch response
 */
export const authFetch = async (url, options = {}, { currentUser, onSessionExpired, onTokenRefresh }) => {
    if (!currentUser?.token) {
        onSessionExpired?.();
        throw new SessionExpiredError('No authentication token');
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            ...options.headers,
            Authorization: `Bearer ${currentUser.token}`,
        },
    });

    // handle 401 unauthorized
    if (response.status === 401) {
        onSessionExpired?.();
        throw new SessionExpiredError();
    }

    // check for token refresh header (Phase 2)
    const newToken = response.headers.get('X-New-Token');
    if (newToken && onTokenRefresh) {
        onTokenRefresh(newToken);
    }

    return response;
};

/**
 * Helper for JSON POST requests
 */
export const authPost = async (url, body, authParams) => {
    return authFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    }, authParams);
};

/**
 * Helper for JSON GET requests
 */
export const authGet = async (url, authParams) => {
    return authFetch(url, {
        method: 'GET',
    }, authParams);
};
